import handleNavigation from '@/utils/navigationUtils';
import logError from '@/utils/logError/logError';
import { staffService } from '@/modules/staff/services/staffService';
import servicesService from '@/modules/services/service/servicesService';
import settingsService from '../services/settingsService';
import { VOICE_CALLS_APP_CODE_NAME } from '../constants/settingsConstants';

let inFlightPromise = null;
let fetchGeneration = 0;

const state = {
  settingsData: {},
  staffCount: 0,
  servicesCount: 0,
  loaded: false,
  error: false,
};

const getters = {
  settingsData: (state) => state.settingsData,
  staffCount: (state) => state.staffCount,
  servicesCount: (state) => state.servicesCount,
  settingsLoaded: (state) => state.loaded,
  settingsError: (state) => state.error,
};

const mutations = {
  setSettingsData(state, settingsData) {
    state.settingsData = settingsData;
  },
  setStaffCount(state, count) {
    state.staffCount = count;
  },
  setServicesCount(state, count) {
    state.servicesCount = count;
  },
  setLoaded(state, loaded) {
    state.loaded = loaded;
  },
  setError(state, error) {
    state.error = error;
  },
};

const settled = (result) => (result.status === 'fulfilled' ? result.value : undefined);

const isCallsAppPaid = (rootGetters) => !!rootGetters['AppsStore/appByCodeName'](VOICE_CALLS_APP_CODE_NAME)
  && rootGetters['AppsStore/purchasedApps'].some((app) => app.app_code_name === VOICE_CALLS_APP_CODE_NAME);

const countActiveStaff = (staff = []) => staff.filter((member) => member.active && !member.deleted).length;

const actions = {
  // Mirrors the legacy SettingsController: every source is optional except settings_page_data.
  async fetchSettingsPageData({ commit, dispatch, rootGetters }, { force = false } = {}) {
    if (inFlightPromise && !force) return inFlightPromise;

    fetchGeneration += 1;
    const generation = fetchGeneration;
    const isStale = () => generation !== fetchGeneration;
    commit('setError', false);
    const promise = (async () => {
      try {
        const loadRoles = rootGetters['BusinessStore/hasFeature']('roles_and_permissions')
          && rootGetters['StaffStore/hasPermission']('can_access_staff_management');

        const results = await Promise.allSettled([
          settingsService.fetchSettingsPageData(),
          loadRoles ? dispatch('RolesAndPermissionsStore/fetchBusinessRoles', undefined, { root: true }) : Promise.resolve(),
          dispatch('ResourcesStore/loadResources', undefined, { root: true }),
          dispatch('AppsStore/getSubscribableApps', undefined, { root: true }),
          settingsService.fetchVoiceCallSettings(),
          staffService.fetchAllStaff(),
          servicesService.fetchServices(),
        ]);
        const [pageData, , , , voiceCallSettings, staff, services] = results.map(settled);
        if (results[0].status === 'rejected') throw results[0].reason;
        // A forced refetch started meanwhile; its result is the newer one.
        if (isStale()) return;

        commit('setSettingsData', {
          ...pageData,
          has_active_subscription: isCallsAppPaid(rootGetters),
          publish_number: voiceCallSettings?.publish_number,
          dedicated_number: voiceCallSettings?.dedicated_number,
          business_roles: loadRoles ? rootGetters['RolesAndPermissionsStore/serverBusinessRoles'] || [] : undefined,
          resources: rootGetters['ResourcesStore/resources'] || [],
        });
        commit('setStaffCount', countActiveStaff(staff));
        commit('setServicesCount', services?.length || 0);
        commit('setLoaded', true);
      } catch (error) {
        logError('Error fetching the settings page data', error);
        if (isStale()) return;
        commit('setLoaded', false);
        commit('setError', true);
      }
    })();

    inFlightPromise = promise;
    promise.finally(() => {
      if (inFlightPromise === promise) inFlightPromise = null;
    });
    return promise;
  },
  backToDashboard() {
    handleNavigation({ name: 'dashboard' });
  },
  openMobileSearch() {
    handleNavigation({ route: 'app.search', queryParams: { type: 'setting' } });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
